import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Box,
} from "@mui/material";
import {
  LanguageRounded,
  CloseRounded,
  CheckRounded,
} from "@mui/icons-material";
import { Custom } from "components";
import { useApp, useTheme } from "contexts";
import { LANGUAGE_LIST } from "utils/constants";

type LanguageDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const LanguageDialog: React.FC<LanguageDialogProps> = ({
  open,
  onClose,
}) => {
  const { theme } = useTheme();
  const { locale, setLocale, t } = useApp();
  const isDark = theme.id === "dark";

  const handleSelectLanguage = (code: string) => {
    setLocale(code);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.color.theme.color,
          color: theme.color.font.color,
          border: `2px solid ${theme.color.border.color}`,
          borderRadius: theme.border.square,
          boxShadow: `6px 6px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
          p: { xs: theme.spacing.xs, sm: theme.spacing.md },
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: theme.spacing.sm,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.color.border.color}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing.sm }}>
          <LanguageRounded
            sx={{ color: theme.color.primary.color, fontSize: theme.font.xl }}
          />
          <Box
            component="span"
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: theme.font.sm,
              fontWeight: theme.font.bold,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {t.languages.dialog_title}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t.languages.dialog_close}
          sx={{
            color: theme.color.font.accent,
            borderRadius: theme.border.square,
            border: `1px solid ${theme.color.border.color}`,
            p: theme.spacing.xxs,
            "&:hover": {
              backgroundColor: theme.color.background.accent,
              color: theme.color.font.color,
            },
          }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: theme.spacing.xs, sm: theme.spacing.md }, mt: 1 }}>
        <Grid container spacing={theme.spacing.sm}>
          {LANGUAGE_LIST.map((item) => {
            const isSelected =
              item.code.toUpperCase() === locale.toUpperCase();
            const langInfo = t.languages?.items?.[item.code] || {
              name: item.nativeName,
              subtitle: "",
            };

            return (
              <Grid item xs={12} sm={6} md={4} key={item.code}>
                <Box
                  onClick={() => handleSelectLanguage(item.code)}
                  sx={{
                    p: theme.spacing.md,
                    cursor: "pointer",
                    backgroundColor: isSelected
                      ? isDark
                        ? "rgba(177, 197, 255, 0.12)"
                        : "rgba(53, 89, 168, 0.08)"
                      : theme.color.background.accent,
                    border: `1px solid ${
                      isSelected
                        ? theme.color.primary.color
                        : theme.color.border.color
                    }`,
                    borderRadius: theme.border.square,
                    boxShadow: isSelected
                      ? `2px 2px 0px 0px ${theme.color.primary.color}`
                      : `1px 1px 0px 0px ${theme.color.border.color}`,
                    transition: "transform 0.12s ease, box-shadow 0.12s ease",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: theme.spacing.sm,
                    "&:hover": {
                      transform: "translate(-2px, -2px)",
                      boxShadow: `4px 4px 0px 0px ${
                        isDark ? "#000000" : "#191b22"
                      }`,
                      borderColor: isDark ? "#ffffff" : "#191b22",
                    },
                    "&:active": {
                      transform: "translate(1px, 1px)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {/* Left Flagpack Flag & Text Details */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: theme.spacing.md,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: theme.spacing.xxs,
                        backgroundColor: theme.color.theme.color,
                        border: `1px solid ${theme.color.border.color}`,
                        borderRadius: theme.border.square,
                        lineHeight: 1,
                      }}
                    >
                      <Custom.Flag
                        code={item.flagCode}
                        size="m"
                        hasBorder={false}
                        alt={`${item.nativeName} flag`}
                      />
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          fontFamily: '"Space Mono", monospace',
                          fontSize: theme.font.xs,
                          fontWeight: theme.font.bold,
                          color: isSelected
                            ? theme.color.primary.color
                            : theme.color.font.color,
                        }}
                      >
                        {item.nativeName}
                      </Box>
                      <Box
                        sx={{
                          fontSize: theme.font.xxs,
                          color: theme.color.font.accent,
                          mt: theme.spacing.xxs,
                        }}
                      >
                        {langInfo.name}
                      </Box>
                      <Box
                        sx={{
                          fontSize: theme.font.xxxs,
                          color: theme.color.font.accent,
                          fontFamily: '"Space Mono", monospace',
                          mt: theme.spacing.xxs,
                          opacity: 0.85,
                        }}
                      >
                        {langInfo.subtitle}
                      </Box>
                    </Box>
                  </Box>

                  {/* Active Check Indicator */}
                  {isSelected && (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: theme.border.square,
                        backgroundColor: theme.color.primary.color,
                        color: theme.color.primary.text,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <CheckRounded sx={{ fontSize: theme.font.md }} />
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

